/**
 * 可用模型页面
 * 显示当前系统可用的所有 AI 模型及其渠道信息
 */

import { LayoutWithSidebar } from "@/components/LayoutWithSidebar";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuthStore } from "@/stores/auth";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import {
  Bot,
  RefreshCw,
  Server,
  CheckCircle,
  XCircle,
  Wallet,
  Network,
  TrendingUp
} from "lucide-react";
import { getQuotaInfo, formatQuotaNumber, type QuotaInfo } from "@/services/topupApi";

interface Channel {
  id: number;
  name: string;
  type: number;
  status: number;
  group: string;
  weight: number;
  balance: number;
  models: string;
}

interface ModelData {
  model: string;
  channels: Channel[];
  total_channels: number;
}

export function AvailableModelsPage() {
  const { accessToken } = useAuthStore();
  const [models, setModels] = useState<ModelData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotaInfo, setQuotaInfo] = useState<QuotaInfo | null>(null);
  const [loadingQuota, setLoadingQuota] = useState(false);

  // 获取可用模型列表
  const fetchAvailableModels = async () => {
    try {
      setLoading(true);
      setError(null);

      // 同时加载额度信息
      try {
        setLoadingQuota(true);
        const quotaResponse = await getQuotaInfo();
        if (quotaResponse.success) {
          setQuotaInfo(quotaResponse.data);
        }
      } catch (error) {
        console.error("获取额度信息失败:", error);
      } finally {
        setLoadingQuota(false);
      }

      // 调用 OneAPI 接口
      const response = await fetch("/api/channels/available-models", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.message || "获取可用模型失败");
      }

      setModels(result.data || []);
      toast.success(`成功获取 ${result.data?.length || 0} 个可用模型`);
    } catch (err: any) {
      console.error("获取可用模型失败:", err);
      setError(err.message || "获取可用模型失败");
      toast.error(err.message || "获取可用模型失败");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAvailableModels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 获取渠道类型名称
  const getChannelTypeName = (type: number) => {
    const types: Record<number, string> = {
      1: "OpenAI",
      2: "API2D",
      3: "Azure",
      14: "Claude",
      15: "PaLM",
      17: "VertexAI",
      18: "ChatGLM",
      19: "ERNIE",
    };
    return types[type] || `类型${type}`;
  };

  // 获取渠道状态
  const getChannelStatus = (status: number) => {
    if (status === 1) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          启用
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
        <XCircle className="h-3 w-3 mr-1" />
        禁用
      </Badge>
    );
  };

  return (
    <LayoutWithSidebar>
      <div className="flex flex-col gap-6 p-6">
        {/* 页面标题 */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">可用模型</h1>
            <p className="text-gray-500 mt-2">查看系统当前可用的所有 AI 模型及其渠道信息</p>
          </div>
          <Button
            onClick={fetchAvailableModels}
            disabled={loading}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            刷新
          </Button>
        </div>

        {/* 错误提示 */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-700">{error}</p>
            </CardContent>
          </Card>
        )}

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Bot className="h-4 w-4" />
                可用模型
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{models.length}</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Server className="h-4 w-4" />
                总渠道数
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">
                {models.reduce((sum, m) => sum + m.total_channels, 0)}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
                <Wallet className="h-4 w-4" />
                我的Token
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-1">
                <p className="text-2xl font-bold text-purple-600">
                  {loadingQuota ? "..." : formatQuotaNumber(quotaInfo?.quota || 0)}
                </p>
                <p className="text-xs text-gray-500">
                  已用: {loadingQuota ? "..." : formatQuotaNumber(quotaInfo?.used_quota || 0)} |
                  请求: {loadingQuota ? "..." : formatQuotaNumber(quotaInfo?.request_count || 0)} 次
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 模型列表 */}
        {loading ? (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
                <p className="ml-3 text-gray-600">加载中...</p>
              </div>
            </CardContent>
          </Card>
        ) : models.length === 0 ? (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <Bot className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">暂无可用模型</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {models.map((modelData) => (
              <Card key={modelData.model}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-50 rounded-lg">
                        <Bot className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">{modelData.model}</CardTitle>
                        <CardDescription>
                          {modelData.total_channels} 个渠道可用
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-sm">
                      <Network className="h-3 w-3 mr-1" />
                      {modelData.total_channels} 渠道
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {modelData.channels.map((channel) => (
                      <div
                        key={channel.id}
                        className="p-4 bg-gray-50 rounded-lg border hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="font-medium text-gray-900">{channel.name}</h4>
                              {getChannelStatus(channel.status)}
                            </div>
                            <div className="flex flex-wrap gap-2 text-xs text-gray-600 mb-2">
                              <span className="flex items-center gap-1">
                                <Server className="h-3 w-3" />
                                {getChannelTypeName(channel.type)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Network className="h-3 w-3" />
                                {channel.group}
                              </span>
                              <span className="flex items-center gap-1">
                                <TrendingUp className="h-3 w-3" />
                                权重: {channel.weight}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 border-t border-gray-200">
                          <p className="text-xs text-gray-500">
                            支持模型: {channel.models}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </LayoutWithSidebar>
  );
}

export default AvailableModelsPage;
